<template>
  <div class="trn-dash">

    <div class="page-header">
      <div>
        <h5 class="page-title">Training Dashboard</h5>
        <p class="page-sub">Compliance, enrolments and certificate status at a glance</p>
      </div>
      <div class="header-actions">
        <router-link :to="$url('/hr/training/courses')" class="btn-primary">
          <font-awesome-icon icon="plus" class="me-1" /> New Course
        </router-link>
        <button class="btn-icon-refresh" @click="load" :disabled="loading" title="Refresh">
          <font-awesome-icon icon="redo" />
        </button>
      </div>
    </div>

    <div v-if="loading" class="full-loader"><div class="spinner"></div></div>

    <template v-else>

      <!-- ── KPI row ─────────────────────────────────────────────────────── -->
      <div class="kpi-row">
        <div class="kpi-card kpi-card--blue">
          <div class="kpi-icon"><font-awesome-icon icon="graduation-cap" /></div>
          <div class="kpi-body">
            <span class="kpi-value">{{ stats.total_courses ?? 0 }}</span>
            <span class="kpi-label">Published Courses</span>
          </div>
        </div>
        <div class="kpi-card kpi-card--green">
          <div class="kpi-icon"><font-awesome-icon icon="check-circle" /></div>
          <div class="kpi-body">
            <span class="kpi-value">{{ stats.compliance_pct ?? 0 }}%</span>
            <span class="kpi-label">Compliance Rate</span>
          </div>
        </div>
        <div class="kpi-card kpi-card--teal">
          <div class="kpi-icon"><font-awesome-icon icon="users" /></div>
          <div class="kpi-body">
            <span class="kpi-value">{{ stats.active_enrolments ?? 0 }}</span>
            <span class="kpi-label">Active Enrolments</span>
          </div>
        </div>
        <div class="kpi-card kpi-card--purple">
          <div class="kpi-icon"><font-awesome-icon icon="award" /></div>
          <div class="kpi-body">
            <span class="kpi-value">{{ stats.completed_this_month ?? 0 }}</span>
            <span class="kpi-label">Completed This Month</span>
          </div>
        </div>
        <div class="kpi-card kpi-card--amber">
          <div class="kpi-icon"><font-awesome-icon icon="exclamation-triangle" /></div>
          <div class="kpi-body">
            <span class="kpi-value">{{ stats.overdue_count ?? 0 }}</span>
            <span class="kpi-label">Overdue</span>
          </div>
        </div>
        <div class="kpi-card kpi-card--red">
          <div class="kpi-icon"><font-awesome-icon icon="id-card" /></div>
          <div class="kpi-body">
            <span class="kpi-value">{{ stats.cert_expiring_30 ?? 0 }}</span>
            <span class="kpi-label">Certs Expiring (30d)</span>
          </div>
        </div>
      </div>

      <!-- ── Row 1: Status donut + Monthly completions ──────────────────── -->
      <div class="chart-row chart-row--2">
        <div class="chart-card">
          <div class="chart-card-header">
            <span class="chart-card-title">Enrolment Status Breakdown</span>
          </div>
          <GChart
            v-if="statusChartData.length > 1"
            type="PieChart"
            :data="statusChartData"
            :options="statusChartOpts"
            style="width:100%;height:260px"
          />
          <div v-else class="chart-empty">No enrolment data yet</div>
        </div>

        <div class="chart-card">
          <div class="chart-card-header">
            <span class="chart-card-title">Monthly Completions — Last 6 Months</span>
          </div>
          <GChart
            v-if="completionsChartData.length > 1"
            type="ColumnChart"
            :data="completionsChartData"
            :options="completionsChartOpts"
            style="width:100%;height:260px"
          />
          <div v-else class="chart-empty">No completion data yet</div>
        </div>
      </div>

      <!-- ── Row 2: Overdue by dept + Upcoming due ──────────────────────── -->
      <div class="table-row">
        <div class="table-card">
          <div class="table-card-header">
            <span class="table-card-title">
              <font-awesome-icon icon="exclamation-triangle" class="me-1 icon-amber" />
              Overdue by Department
            </span>
          </div>
          <div v-if="!overdueByDept.length" class="table-empty">No overdue training.</div>
          <table v-else class="mini-table">
            <thead><tr><th>Department</th><th class="num-col">Overdue</th></tr></thead>
            <tbody>
              <tr v-for="row in overdueByDept" :key="row.department">
                <td>{{ row.department }}</td>
                <td class="num-col">
                  <span class="badge-pill badge-pill--amber">{{ row.overdue_count }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="table-card">
          <div class="table-card-header">
            <span class="table-card-title">
              <font-awesome-icon icon="calendar-alt" class="me-1 icon-blue" />
              Due in Next 7 Days
            </span>
          </div>
          <div v-if="!upcomingDue.length" class="table-empty">Nothing due in the next 7 days.</div>
          <table v-else class="mini-table">
            <thead><tr><th>Employee</th><th>Course</th><th>Due</th></tr></thead>
            <tbody>
              <tr v-for="e in upcomingDue" :key="e.id">
                <td>{{ employeeName(e.employee) }}</td>
                <td>{{ e.course?.title }}</td>
                <td>
                  <span :class="['days-pill', daysClass(e.due_date)]">{{ formatDate(e.due_date) }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ── Low completion courses ──────────────────────────────────────── -->
      <div class="table-card mt-16" v-if="lowCompletionCourses.length">
        <div class="table-card-header">
          <span class="table-card-title">
            <font-awesome-icon icon="chart-bar" class="me-1 icon-red" />
            Courses with Low Completion (&lt;30%)
          </span>
        </div>
        <table class="mini-table">
          <thead><tr><th>Course</th><th class="num-col">Enrolled</th><th class="num-col">Completed</th><th class="num-col">Rate</th></tr></thead>
          <tbody>
            <tr v-for="c in lowCompletionCourses" :key="c.id">
              <td class="fw-600">{{ c.title }}</td>
              <td class="num-col">{{ c.total }}</td>
              <td class="num-col">{{ c.completed }}</td>
              <td class="num-col">
                <span class="badge-pill badge-pill--red">{{ c.total > 0 ? Math.round(c.completed / c.total * 100) : 0 }}%</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

    </template>
  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';

const STATUS_COLORS = {
  not_started:     '#94a3b8',
  in_progress:     '#3b82f6',
  overdue:         '#f97316',
  pending_grading: '#a855f7',
  failed:          '#ef4444',
  completed:       '#22c55e',
  expired:         '#64748b',
  waived:          '#0d9488',
  withdrawn:       '#6b7280',
};

export default {
  name: 'TrainingDashboard',
  setup() { return { toast: useToast() }; },
  data() { return { loading: false, stats: {} }; },
  computed: {
    overdueByDept()      { return this.stats.overdue_by_dept          ?? []; },
    upcomingDue()        { return this.stats.upcoming_due             ?? []; },
    lowCompletionCourses(){ return this.stats.low_completion_courses  ?? []; },

    statusChartData() {
      const breakdown = this.stats.status_breakdown ?? {};
      const rows = Object.entries(breakdown).map(([status, count]) => [
        status.replace(/_/g, ' '), Number(count)
      ]);
      return rows.length ? [['Status', 'Count'], ...rows] : [['Status', 'Count']];
    },
    completionsChartData() {
      const rows = (this.stats.monthly_completions ?? []).map(m => [m.month, m.count]);
      return rows.length ? [['Month', 'Completions'], ...rows] : [['Month', 'Completions']];
    },

    statusChartOpts() {
      return {
        pieHole: 0.45,
        colors: Object.values(STATUS_COLORS),
        legend: { position: 'right', textStyle: { fontSize: 11, color: '#7a90b0' } },
        chartArea: { width: '70%', height: '85%' },
        backgroundColor: 'transparent',
      };
    },
    completionsChartOpts() {
      return {
        colors: ['#004AAD'],
        legend: { position: 'none' },
        chartArea: { width: '82%', height: '72%' },
        hAxis: { textStyle: { fontSize: 10, color: '#7a90b0' } },
        vAxis: { textStyle: { fontSize: 11, color: '#7a90b0' }, minValue: 0, format: '0', gridlines: { color: '#f0f4f8' } },
        backgroundColor: 'transparent',
        bar: { groupWidth: '60%' },
      };
    },
  },
  created() { this.load(); },
  methods: {
    async load() {
      this.loading = true;
      try {
        const { data } = await axios.get('/training/dashboard');
        this.stats = data.data ?? {};
      } catch { this.toast.error('Failed to load training dashboard.'); }
      finally { this.loading = false; }
    },
    formatDate(d) {
      return d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—';
    },
    employeeName(e) { return e ? `${e.first_name} ${e.last_name}` : '—'; },
    daysLeft(d)  { return d ? Math.ceil((new Date(d) - new Date()) / 86400000) : null; },
    daysClass(d) {
      const n = this.daysLeft(d);
      if (n === null) return '';
      if (n <= 1)  return 'days-pill--red';
      if (n <= 3)  return 'days-pill--amber';
      return 'days-pill--green';
    },
  },
};
</script>

<style scoped>
.trn-dash { padding-bottom: 32px; }
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:24px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }
.header-actions { display:flex; align-items:center; gap:10px; }
.me-1 { margin-right:4px; }
.mt-16 { margin-top:16px; }
.full-loader { display:flex; align-items:center; justify-content:center; min-height:300px; }

/* KPI */
.kpi-row { display:grid; grid-template-columns:repeat(6, 1fr); gap:14px; margin-bottom:20px; }
.kpi-card { display:flex; align-items:center; gap:12px; background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:16px 18px; position:relative; overflow:hidden; }
.kpi-card::before { content:''; position:absolute; top:0; left:0; width:4px; height:100%; border-radius:4px 0 0 4px; }
.kpi-card--blue::before   { background:#004AAD; }
.kpi-card--teal::before   { background:#0d9488; }
.kpi-card--green::before  { background:#22c55e; }
.kpi-card--purple::before { background:#7c3aed; }
.kpi-card--amber::before  { background:#f97316; }
.kpi-card--red::before    { background:#dc2626; }
.kpi-icon { width:38px; height:38px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
.kpi-card--blue   .kpi-icon { background:#eff6ff; color:#004AAD; }
.kpi-card--teal   .kpi-icon { background:#f0fdfa; color:#0d9488; }
.kpi-card--green  .kpi-icon { background:#f0fdf4; color:#16a34a; }
.kpi-card--purple .kpi-icon { background:#f5f3ff; color:#7c3aed; }
.kpi-card--amber  .kpi-icon { background:#fff7ed; color:#f97316; }
.kpi-card--red    .kpi-icon { background:#fef2f2; color:#dc2626; }
.kpi-value { display:block; font-size:20px; font-weight:800; color:#1a2b45; line-height:1.1; }
.kpi-label { display:block; font-size:11px; color:#7a90b0; margin-top:3px; }

/* Charts */
.chart-row { display:grid; gap:16px; margin-bottom:20px; }
.chart-row--2 { grid-template-columns:1fr 1fr; }
.chart-card { background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:20px; }
.chart-card-header { display:flex; align-items:baseline; justify-content:space-between; margin-bottom:12px; }
.chart-card-title  { font-size:14px; font-weight:700; color:#1a2b45; }
.chart-empty { display:flex; align-items:center; justify-content:center; height:200px; color:#aab8cc; font-size:13px; }

/* Tables */
.table-row { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
.table-card { background:#fff; border:1px solid #e4eaf2; border-radius:14px; overflow:hidden; }
.table-card-header { display:flex; align-items:center; justify-content:space-between; padding:16px 20px; border-bottom:1px solid #f0f4f8; }
.table-card-title  { font-size:14px; font-weight:700; color:#1a2b45; display:flex; align-items:center; gap:6px; }
.table-empty { padding:28px 20px; text-align:center; color:#aab8cc; font-size:13px; }
.mini-table { width:100%; border-collapse:collapse; }
.mini-table th { padding:9px 16px; font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; background:#f8fafc; border-bottom:1px solid #e4eaf2; text-align:left; }
.mini-table td { padding:11px 16px; font-size:13px; color:#1a2b45; border-bottom:1px solid #f0f4f8; vertical-align:middle; }
.mini-table tbody tr:last-child td { border-bottom:none; }
.num-col { text-align:right; }
.fw-600 { font-weight:600; }

/* Pills */
.badge-pill { display:inline-block; font-size:11.5px; font-weight:700; border-radius:20px; padding:3px 10px; }
.badge-pill--amber { background:#fff7ed; color:#c2410c; }
.badge-pill--red   { background:#fee2e2; color:#dc2626; }
.days-pill { display:inline-block; font-size:11.5px; font-weight:700; border-radius:20px; padding:2px 9px; }
.days-pill--green { background:#dcfce7; color:#15803d; }
.days-pill--amber { background:#fff7ed; color:#c2410c; }
.days-pill--red   { background:#fee2e2; color:#dc2626; }

/* Icons */
.icon-amber { color:#f97316; }
.icon-red   { color:#dc2626; }
.icon-blue  { color:#004AAD; }

/* Buttons */
.btn-primary { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; border:none; font-size:13px; font-weight:500; padding:9px 18px; border-radius:8px; cursor:pointer; text-decoration:none; }
.btn-primary:hover { background:#0056d2; }
.btn-icon-refresh { width:38px; height:38px; border:1.5px solid #dde5f0; border-radius:8px; background:#fff; color:#7a90b0; display:flex; align-items:center; justify-content:center; cursor:pointer; font-size:14px; }
.btn-icon-refresh:hover:not(:disabled) { border-color:#004AAD; color:#004AAD; }
.btn-icon-refresh:disabled { opacity:.5; cursor:not-allowed; }

/* Spinner */
.spinner { width:36px; height:36px; border:3px solid #e4eaf2; border-top-color:#004AAD; border-radius:50%; animation:spin .7s linear infinite; }
@keyframes spin { to { transform:rotate(360deg); } }

@media (max-width:1400px) { .kpi-row { grid-template-columns:repeat(3, 1fr); } }
@media (max-width:1024px) { .chart-row--2, .table-row { grid-template-columns:1fr; } }
@media (max-width:640px)  { .kpi-row { grid-template-columns:1fr 1fr; } }
</style>
